import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';

const ARIA_LOGO = "/screenshots/ariaone-logo_white.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { 
    name: 'Apps', 
    dropdown: [
      { name: 'Lumina', path: '/lumina', desc: 'Clinical Intelligence' },
      { name: 'RevQ', path: '/revq', desc: 'Billing Intelligence' },
      { name: 'Echo', path: '/echo', desc: 'Scheduling Intelligence' },
    ]
  },
  { name: 'Integrations', path: '/integrations' },
  { name: 'Contact', path: '/contact' },
];

const footerLinks = {
  products: [
    { name: 'Lumina', path: '/lumina' },
    { name: 'RevQ', path: '/revq' },
    { name: 'Echo', path: '/echo' },
    { name: 'Integrations', path: '/integrations' },
  ],
  company: [
    { name: 'About', path: '#about' },
    { name: 'Careers', path: '#careers' },
    { name: 'Contact', path: '/contact' },
  ],
  legal: [
    { name: 'Privacy', path: '#privacy' },
    { name: 'Terms', path: '#terms' },
  ],
};

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-aria-border" style={{ borderWidth: '0.5px' }}>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-[60px]">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img src={ARIA_LOGO} alt="Aria One" className="h-8" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.dropdown ? (
                <div 
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => setProductsOpen(true)}
                  onMouseLeave={() => setProductsOpen(false)}
                >
                  <button 
                    className="nav-link text-sm flex items-center gap-1"
                    data-testid="nav-products-dropdown"
                  >
                    {link.name}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <AnimatePresence>
                    {productsOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-2 w-56 bg-aria-card border border-aria-border rounded-xl p-2 shadow-xl"
                      >
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.name}
                            to={item.path}
                            className="block px-4 py-3 rounded-lg hover:bg-aria-elevated transition-colors"
                            data-testid={`nav-dropdown-${item.name.toLowerCase()}`}
                          >
                            <span className="text-white text-sm font-medium">{item.name}</span>
                            <span className="block text-aria-text-muted text-xs mt-0.5">{item.desc}</span>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link 
                  key={link.name}
                  to={link.path}
                  className={`nav-link text-sm ${location.pathname === link.path ? 'text-white' : ''} ${link.temp ? 'text-amber-400 hover:text-amber-300' : ''}`}
                  data-testid={`nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                  {link.temp && <span className="ml-1 text-[10px] align-super opacity-60">tmp</span>}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <button 
              className="btn-ghost px-4 h-9 rounded-lg text-white text-sm font-medium"
              data-testid="header-request-demo-btn"
            >
              Request demo
            </button>
            <button 
              className="btn-primary px-4 h-9 rounded-lg text-black text-sm font-medium"
              data-testid="header-get-started-btn"
            >
              Get started
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden"
            >
              <nav className="py-4 flex flex-col gap-2">
                {navLinks.map((link) => (
                  link.dropdown ? (
                    <div key={link.name}>
                      <span className="block px-4 py-2 text-aria-text-muted text-xs uppercase tracking-wider">
                        {link.name}
                      </span>
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-4 py-2 text-white hover:text-white/70"
                          data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      key={link.name}
                      to={link.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-white hover:text-white/70"
                      data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                    >
                      {link.name}
                    </Link>
                  )
                ))}
                <div className="flex flex-col gap-2 px-4 pt-4 border-t border-aria-border mt-2">
                  <button className="btn-ghost w-full h-10 rounded-lg text-white text-sm font-medium">
                    Request demo
                  </button>
                  <button className="btn-primary w-full h-10 rounded-lg text-black text-sm font-medium">
                    Get started
                  </button>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-aria-bg border-t border-aria-border py-12 md:py-20" style={{ borderWidth: '0.5px' }}>
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <img src={ARIA_LOGO} alt="Aria One" className="h-8 mb-4" />
            <p className="text-aria-text-secondary text-sm leading-relaxed">
              The AI platform built for healthcare. Streamline clinical documentation, billing, and scheduling.
            </p>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-white text-sm font-medium mb-4">Products</h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-aria-text-secondary text-sm hover:text-white transition-colors"
                    data-testid={`footer-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white text-sm font-medium mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-aria-text-secondary text-sm hover:text-white transition-colors"
                    data-testid={`footer-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white text-sm font-medium mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link 
                    to={link.path} 
                    className="text-aria-text-secondary text-sm hover:text-white transition-colors"
                    data-testid={`footer-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="mt-12 pt-8 border-t border-aria-border">
          <p className="text-aria-text-muted text-xs text-center">
            © 2026 Aria One. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  const location = useLocation();
  const hideFooter = ['/', '/archive'].includes(location.pathname);
  return (
    <div className="min-h-screen bg-aria-bg">
      <Header />
      <main className="pt-[60px]">
        {children}
      </main>
      {!hideFooter && <Footer />}
    </div>
  );
};

export default Layout;
